'use client';

import { useEffect } from 'react';

import { useIsUserVerified } from '~/hooks/useIsUserVerified';

export type UseRedirectUnverifiedUserWithoutWorkspacesParams = {
  isWorkspacesFetched: boolean;
  onUnverified: () => void;
  workspaceCount: number | undefined;
};

export type UseRedirectUnverifiedUserWithoutWorkspacesReturn = {
  /**
   * True while this hook may still send the user to `/verify-user`, so hosts
   * should keep showing a spinner instead of the no-workspace empty state.
   */
  isWaitingOnVerification: boolean;
  shouldRedirectToVerifyUser: boolean;
};

/**
 * Sends unverified users with zero workspaces to the plugin verify-user
 * surface. Users who already belong to a workspace keep using the plugin;
 * only the create/join path is blocked, matching core.
 */
export const useRedirectUnverifiedUserWithoutWorkspaces = ({
  isWorkspacesFetched,
  onUnverified,
  workspaceCount,
}: UseRedirectUnverifiedUserWithoutWorkspacesParams): UseRedirectUnverifiedUserWithoutWorkspacesReturn => {
  const { isFetched, userEmailIsVerified } = useIsUserVerified();

  const hasNoWorkspaces = isWorkspacesFetched && workspaceCount === 0;
  const shouldRedirectToVerifyUser = hasNoWorkspaces && isFetched && userEmailIsVerified === false;
  const isWaitingOnVerification = hasNoWorkspaces && (!isFetched || shouldRedirectToVerifyUser);

  useEffect(() => {
    if (shouldRedirectToVerifyUser) {
      onUnverified();
    }
  }, [onUnverified, shouldRedirectToVerifyUser]);

  return {
    isWaitingOnVerification,
    shouldRedirectToVerifyUser,
  };
};
